import { useCreateHeliosDataTablesRecordMutation, useUpdateHeliosDataTablesRecordMutation } from 'store/services/helios/dataHelios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { processRowData } from 'utils/helios/row';
import type { TABLE_CELL_TYPE } from 'types/enums';
import type { DataSchema, Schema } from 'types';
import type { HeliosColumnsCellDataForBe } from 'types/helios/agendaListing';
import type { HeliosRecordDataSection } from 'types/dataHelios';

interface UseHeliosRecordDetailRowActionsProps {
  recordId: string;
  templateId: string;
  dataSchema: DataSchema;
  handleCancelEditRow: (rowId: string) => void;
  fetchDataTables: () => Promise<void>;
  data: Schema<string, DataSchema>[];
  section: HeliosRecordDataSection;
}

export const useHeliosRecordDetailRowActions = ({
  recordId,
  templateId,
  dataSchema,
  handleCancelEditRow,
  fetchDataTables,
  data,
  section,
}: UseHeliosRecordDetailRowActionsProps) => {
  const { t } = useTranslation();

  const [idsForEdit, setIdsForEdit] = useState<string[]>([]);
  const [idsForMobileInRowEdit, setIdsForMobileInRowEdit] = useState<string[]>([]);

  const [createDataRecord, { isLoading: isCreateLoading }] = useCreateHeliosDataTablesRecordMutation();
  const [updateDataRecord] = useUpdateHeliosDataTablesRecordMutation();

  const isEditable = useMemo(() => idsForEdit.length > 0, [idsForEdit]);

  const handleCancelEditRowWithMobile = useCallback(
    (rowId: string) => {
      setIdsForMobileInRowEdit((prev) => prev.filter((id) => id !== rowId));
      handleCancelEditRow(rowId);
    },
    [handleCancelEditRow]
  );

  useEffect(() => {
    if (!section.entry?.data) {
      setIdsForEdit([]);
      return;
    }

    const dataMap = Object.fromEntries(data.map((item) => [item.id, item.identifier]));

    const editableRows = section.entry.data
      .filter((record) => record.actions.editInRow)
      .map((record) => dataMap[record.recordNumber.toString()])
      .filter(Boolean);

    setIdsForEdit(editableRows);
  }, [section, data]);

  const saveCreatedRow = useCallback(
    async (row: Record<string, { value: string | number | boolean | object; type: TABLE_CELL_TYPE }>) => {
      const rowId = row.id.value as string;

      try {
        const columns = processRowData({ row, dataSchema });
        const res = await createDataRecord({
          templateId,
          masterRecordNumber: recordId,
          columns: columns as HeliosColumnsCellDataForBe[],
        });
        if (!('error' in res)) {
          toast.success(t('helios.listing.success.createRow'));
          handleCancelEditRow(rowId);
          await fetchDataTables();
        }
      } catch {
        toast.error(t('common.error'));
      }
    },
    [createDataRecord, dataSchema, fetchDataTables, handleCancelEditRow, recordId, t, templateId]
  );

  const saveEditedRow = useCallback(
    async (row: Record<string, { value: string | number | boolean | object; type: TABLE_CELL_TYPE }>) => {
      const rowId = row.id.value as string;
      const parts = rowId.split('__');
      const actualRowId = parts[parts.length - 1];

      try {
        const columns = processRowData({ row, dataSchema });
        const res = await updateDataRecord({
          templateId,
          masterRecordNumber: recordId,
          recordNumber: Number(actualRowId),
          columns: columns as HeliosColumnsCellDataForBe[],
        });

        if (!('error' in res)) {
          toast.success(t('helios.listing.success.editRow'));
          handleCancelEditRowWithMobile(rowId);
          setIdsForEdit([]);
          setIdsForMobileInRowEdit([]);
          await fetchDataTables();
        }
      } catch {
        toast.error(t('common.error'));
      }
    },
    [dataSchema, updateDataRecord, templateId, handleCancelEditRowWithMobile, fetchDataTables, recordId, t]
  );

  const handleSaveEditedRow = useCallback(
    (row: Record<string, { value: string | number | boolean | object; type: TABLE_CELL_TYPE }>) =>
      ((row.id.value as string).startsWith('new') ? saveCreatedRow : saveEditedRow)(row),
    [saveCreatedRow, saveEditedRow]
  );

  return {
    idsForEdit,
    setIdsForEdit,
    idsForMobileInRowEdit,
    setIdsForMobileInRowEdit,
    isEditable,
    isCreateLoading,
    handleSaveEditedRow,
    handleCancelEditRowWithMobile,
  };
};
